import type { ComponentType } from 'react';
import { DocType } from '@enums/doc-type.enum';
import type { IconProps } from '@components/common/Icons/types';
import { MessageKeys, TypedFormatMessageFn } from '@app-types/intl';
import {
  DocumentReviewType,
  EvidenceSectionName,
  EvidenceSectionNameMap,
  EvidenceTask,
  EvidenceTaskName,
  EvidenceTaskNameMap,
  EvidenceTaskType,
} from '@api/brokerCases/types';
import { ProofOfIdentityIcon } from '@components/common/Icons/ProofOfIdentityIcon';
import { EmploymentIncomeIcon } from '@components/common/Icons/EmploymentIncomeIcon';
import { BankStatementsIcon } from '@components/common/Icons/BankStatementsIcon';
import { ProofOfDepositIcon } from '@components/common/Icons/ProofOfDepositIcon';
import { OtherDocumentsIcon } from '@components/common/Icons/OtherDocumentsIcon';
import { FurtherInformationRequiredIcon } from '@components/common/Icons/FurtherInformationRequiredIcon';

export interface EvidenceSectionConfig {
  labelId: MessageKeys;
  descriptionId: MessageKeys;
  icon: ComponentType<IconProps>;
}

export const EVIDENCE_SECTION_CONFIG: Record<EvidenceSectionName, EvidenceSectionConfig> = {
  [EvidenceSectionNameMap.PROOF_OF_IDENTITY]: {
    labelId: 'evidence.section.proofOfIdentity.label',
    descriptionId: 'evidence.section.proofOfIdentity.description',
    icon: ProofOfIdentityIcon,
  },
  [EvidenceSectionNameMap.EMPLOYMENT_INCOME]: {
    labelId: 'evidence.section.employmentIncome.label',
    descriptionId: 'evidence.section.employmentIncome.description',
    icon: EmploymentIncomeIcon,
  },
  [EvidenceSectionNameMap.BANK_STATEMENTS]: {
    labelId: 'evidence.section.bankStatements.label',
    descriptionId: 'evidence.section.bankStatements.description',
    icon: BankStatementsIcon,
  },
  [EvidenceSectionNameMap.PROOF_OF_DEPOSIT]: {
    labelId: 'evidence.section.proofOfDeposit.label',
    descriptionId: 'evidence.section.proofOfDeposit.description',
    icon: ProofOfDepositIcon,
  },
  [EvidenceSectionNameMap.FURTHER_INFORMATION_REQUIRED]: {
    labelId: 'evidence.section.furtherInformationRequired.label',
    descriptionId: 'evidence.section.furtherInformationRequired.description',
    icon: FurtherInformationRequiredIcon,
  },
  [EvidenceSectionNameMap.OTHER_DOCUMENTS]: {
    labelId: 'evidence.section.otherDocuments.label',
    descriptionId: 'evidence.section.otherDocuments.description',
    icon: OtherDocumentsIcon,
  },
};

export function getEvidenceSectionLabel(name: EvidenceSectionName, formatMessage: TypedFormatMessageFn): string {
  return formatMessage({ id: EVIDENCE_SECTION_CONFIG[name].labelId });
}

export function getEvidenceSectionDescription(name: EvidenceSectionName, formatMessage: TypedFormatMessageFn): string {
  return formatMessage({ id: EVIDENCE_SECTION_CONFIG[name].descriptionId });
}

interface EvidenceTaskConfig {
  labelId: MessageKeys;
  descriptionId: MessageKeys;
}

export const EVIDENCE_TASK_CONFIG: Record<EvidenceTaskName, EvidenceTaskConfig> = {
  [EvidenceTaskNameMap.BANK_STATEMENT_OR_GIFTED_DEPOSIT]: {
    labelId: 'evidence.task.bankStatementOrGiftedDeposit.label',
    descriptionId: 'evidence.task.bankStatementOrGiftedDeposit.description',
  },
  // In the further-information section, the list row title uses `task.payload.description` when the API sends it.
  [EvidenceTaskNameMap.FURTHER_INFORMATION_REQUIRED]: {
    labelId: 'evidence.task.furtherInformationRequired.label',
    descriptionId: 'evidence.task.furtherInformationRequired.description',
  },
  // OTHER_DOCUMENTS: tooltip uses `task.payload.description` when the API sends it; this string is the fallback only.
  [EvidenceTaskNameMap.OTHER_DOCUMENTS]: {
    labelId: 'evidence.task.otherDocuments.label',
    descriptionId: 'evidence.task.otherDocuments.description',
  },
  [EvidenceTaskNameMap.PROOF_OF_IDENTITY]: {
    labelId: 'evidence.task.proofOfIdentity.label',
    descriptionId: 'evidence.task.proofOfIdentity.description',
  },
  [EvidenceTaskNameMap.SHARE_CODE_CONFIRMING_VISA_STATUS]: {
    labelId: 'evidence.task.shareCodeConfirmingVisaStatus.label',
    descriptionId: 'evidence.task.shareCodeConfirmingVisaStatus.description',
  },
  [EvidenceTaskNameMap.LATEST_3_MONTHS_PAYSLIPS]: {
    labelId: 'evidence.task.latest3MonthsPayslips.label',
    descriptionId: 'evidence.task.latest3MonthsPayslips.description',
  },
  [EvidenceTaskNameMap.LAST_YEARS_SA302]: {
    labelId: 'evidence.task.lastYearsSa302.label',
    descriptionId: 'evidence.task.lastYearsSa302.description',
  },
  [EvidenceTaskNameMap.LAST_3_MONTHS_BANK_STATEMENTS]: {
    labelId: 'evidence.task.last3MonthsBankStatements.label',
    descriptionId: 'evidence.task.last3MonthsBankStatements.description',
  },
  [EvidenceTaskNameMap.CURRENT_CONTRACT]: {
    labelId: 'evidence.task.currentContract.label',
    descriptionId: 'evidence.task.currentContract.description',
  },
};

function humanizeUnknownEvidenceTaskName(name: string): string {
  const segment = name.split('/').pop() ?? name;
  return segment
    .replace(/^individual-/, '')
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getEvidenceTaskLabel(name: EvidenceTaskName, formatMessage: TypedFormatMessageFn): string {
  const entry = EVIDENCE_TASK_CONFIG[name];
  return entry ? formatMessage({ id: entry.labelId }) : humanizeUnknownEvidenceTaskName(name);
}

export function getEvidenceTaskDescription(name: EvidenceTaskName, formatMessage: TypedFormatMessageFn): string {
  const entry = EVIDENCE_TASK_CONFIG[name];
  return entry ? formatMessage({ id: entry.descriptionId }) : formatMessage({ id: 'evidence.task.defaultDescription' });
}

export function getEvidenceTaskListPrimaryLabel(
  task: EvidenceTask,
  sectionName: EvidenceSectionName,
  formatMessage: TypedFormatMessageFn,
): string {
  if (
    sectionName === EvidenceSectionNameMap.FURTHER_INFORMATION_REQUIRED &&
    task.payload.taskType === EvidenceTaskType.UPLOAD_FILE
  ) {
    const fromPayload = task.payload.description?.trim();
    if (fromPayload) {
      return fromPayload;
    }
  }
  return getEvidenceTaskLabel(task.name, formatMessage);
}

export function getEvidenceTaskTooltipDescription(
  task: EvidenceTask,
  sectionName: EvidenceSectionName,
  formatMessage: TypedFormatMessageFn,
): string {
  if (task.payload.taskType === EvidenceTaskType.UPLOAD_FILE) {
    const fromPayload = task.payload.description?.trim();
    if (task.name === EvidenceTaskNameMap.OTHER_DOCUMENTS) {
      return fromPayload || getEvidenceTaskDescription(task.name, formatMessage);
    }
    if (sectionName === EvidenceSectionNameMap.FURTHER_INFORMATION_REQUIRED && fromPayload) {
      return getEvidenceTaskDescription(task.name, formatMessage);
    }
  }
  return getEvidenceTaskDescription(task.name, formatMessage);
}

/** Maps evidence task name to DocType for upload API */
export const EVIDENCE_TASK_NAME_TO_DOC_TYPE: Partial<Record<EvidenceTaskName, DocType>> = {
  [EvidenceTaskNameMap.BANK_STATEMENT_OR_GIFTED_DEPOSIT]: DocType.PROOF_OF_DEPOSIT,
  [EvidenceTaskNameMap.OTHER_DOCUMENTS]: DocType.OTHER,
  [EvidenceTaskNameMap.PROOF_OF_IDENTITY]: DocType.PROOF_OF_IDENTITY,
  [EvidenceTaskNameMap.LATEST_3_MONTHS_PAYSLIPS]: DocType.PROOF_OF_INCOME,
  [EvidenceTaskNameMap.LAST_YEARS_SA302]: DocType.PROOF_OF_INCOME,
  [EvidenceTaskNameMap.LAST_3_MONTHS_BANK_STATEMENTS]: DocType.BANK_STATEMENT,
  [EvidenceTaskNameMap.CURRENT_CONTRACT]: DocType.PROOF_OF_INCOME,
};

export const DOCUMENT_REVIEW_TYPE_TO_RELEVANT_TASKS: Record<DocumentReviewType, EvidenceTaskName[]> = {
  [DocumentReviewType.CUSTOMER_FINANCES]: [
    EvidenceTaskNameMap.LAST_3_MONTHS_BANK_STATEMENTS,
    EvidenceTaskNameMap.LATEST_3_MONTHS_PAYSLIPS,
  ],
};

export function getDocTypeForTask(taskName: EvidenceTaskName): DocType {
  return EVIDENCE_TASK_NAME_TO_DOC_TYPE[taskName] ?? DocType.OTHER;
}

export function getUploadDocType(taskName: EvidenceTaskName, payloadType?: DocType): DocType {
  if (taskName === EvidenceTaskNameMap.PROOF_OF_IDENTITY) {
    return getDocTypeForTask(taskName);
  }
  return payloadType ?? getDocTypeForTask(taskName);
}

export interface EvidenceUploadFileListOption {
  label: string;
  value: string;
}

export function getDescriptionOptionsByTaskName(
  taskName: EvidenceTaskName,
  formatMessage: TypedFormatMessageFn,
): EvidenceUploadFileListOption[] | undefined {
  if (taskName !== EvidenceTaskNameMap.BANK_STATEMENT_OR_GIFTED_DEPOSIT) {
    return undefined;
  }

  const depositStatement = formatMessage({ id: 'evidence.upload.description.depositStatement' });
  const giftedDepositConfirmation = formatMessage({ id: 'evidence.upload.description.giftedDepositConfirmation' });

  return [
    { label: depositStatement, value: depositStatement },
    { label: giftedDepositConfirmation, value: giftedDepositConfirmation },
  ];
}
